import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import LoadingSpinner from '../../components/LoadingSpinner'
import GooglePlacesAutocomplete from '../../components/ui/GooglePlacesAutocomplete'
import toast from 'react-hot-toast'
import { api } from '../../services/api'
import DocumentManagementTab from '../../components/provider/DocumentManagementTab'
import {
  UserCircleIcon,
  BuildingOfficeIcon,
  PhoneIcon,
  EnvelopeIcon,
  GlobeAltIcon,
  MapPinIcon,
  PencilIcon,
  CheckBadgeIcon,
  XMarkIcon,
  ClipboardDocumentIcon,
  DocumentTextIcon,
  ClockIcon
} from '@heroicons/react/24/outline'

interface WeeklySchedule {
  Monday: { enabled: boolean; ranges: { start: string; end: string }[] };
  Tuesday: { enabled: boolean; ranges: { start: string; end: string }[] };
  Wednesday: { enabled: boolean; ranges: { start: string; end: string }[] };
  Thursday: { enabled: boolean; ranges: { start: string; end: string }[] };
  Friday: { enabled: boolean; ranges: { start: string; end: string }[] };
  Saturday: { enabled: boolean; ranges: { start: string; end: string }[] };
  Sunday: { enabled: boolean; ranges: { start: string; end: string }[] };
}

interface CustomerReview {
  id: string;
  rating: number;
  comment: string;
  reviewedBy: {
    firstName: string;
    lastName: string;
  };
  createdAt: string;
  caseId?: string;
  caseNumber?: string;
}

interface ProviderProfile {
  id: string
  email: string
  firstName: string
  lastName: string
  phone?: string
  role: string
  isVerified: boolean
  isApproved: boolean
  serviceProvider?: {
    id: string
    companyName: string
    description?: string
    contactEmail?: string
    website?: string
    address?: string
    serviceAreas?: Array<{ state: string; stateName: string; cities: string[] }>
    rating: number
    type?: string
    specialization?: string[]
    experience?: string
    certifications?: string[]
    businessHours?: {
      monday?: string;
      tuesday?: string;
      wednesday?: string;
      thursday?: string;
      friday?: string;
      saturday?: string;
      sunday?: string;
    };
    pricing?: {
      baseRate?: number;
      perKmRate?: number;
      emergencyRate?: number;
    };
  }
}

const ProviderProfilePage: React.FC = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [profile, setProfile] = useState<ProviderProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(false)
  const [editData, setEditData] = useState<Partial<ProviderProfile>>({})
  const [saving, setSaving] = useState(false)
  const [activeTab, setActiveTab] = useState<'profile' | 'documents'>('profile')
  const [weeklySchedule, setWeeklySchedule] = useState<WeeklySchedule | null>(null)
  const [scheduleLoading, setScheduleLoading] = useState(false)
  const [reviews, setReviews] = useState<CustomerReview[]>([])
  const [reviewsLoading, setReviewsLoading] = useState(false)

  useEffect(() => {
    fetchProfile()
    fetchWeeklySchedule()
    fetchReviews()
  }, [])

  const fetchProfile = async () => {
    try {
      const response = await api.get('/provider-dashboard/profile')
      const apiData = response.data

      // Map the API response to the expected interface structure
      const mappedProfile: ProviderProfile = {
        id: apiData.user.id,
        email: apiData.user.email,
        firstName: apiData.user.firstName,
        lastName: apiData.user.lastName,
        phone: apiData.user.phoneNumber,
        role: user?.role || 'PROVIDER', // Get role from auth context since API doesn't include it
        isVerified: apiData.user.isVerified,
        isApproved: apiData.user.isApproved,
        serviceProvider: apiData.serviceProvider ? {
          ...apiData.serviceProvider,
          contactEmail: apiData.serviceProvider.contactEmail || apiData.user.email,
          website: apiData.serviceProvider.website || null,
          businessHours: apiData.serviceProvider.businessHours || {
            monday: '9:00 AM - 5:00 PM',
            tuesday: '9:00 AM - 5:00 PM',
            wednesday: '9:00 AM - 5:00 PM',
            thursday: '9:00 AM - 5:00 PM',
            friday: '9:00 AM - 5:00 PM',
            saturday: '9:00 AM - 5:00 PM',
            sunday: 'Closed'
          },
          type: apiData.serviceProvider.serviceType,
          specialization: [],
          experience: apiData.serviceProvider.description || '',
          rating: apiData.serviceProvider.rating || 0
        } : undefined
      }

      setProfile(mappedProfile)
      setEditData(mappedProfile)
    } catch (err: any) {
      toast.error(err.response?.data?.message || err.message || 'Failed to load profile')
    } finally {
      setLoading(false)
    }
  }

  const fetchWeeklySchedule = async () => {
    try {
      setScheduleLoading(true)
      const response = await api.get('/provider-dashboard/schedule/weekly')
      // Extract the schedule from the response { schedule: ... }
      setWeeklySchedule(response.data.schedule)
    } catch (err: any) {
      console.error('Failed to load weekly schedule:', err)
      // Don't show toast error for schedule as it's not critical for profile page
    } finally {
      setScheduleLoading(false)
    }
  }

  const fetchReviews = async () => {
    try {
      setReviewsLoading(true)
      const response = await api.get('/provider-dashboard/reviews')
      setReviews(response.data.reviews || [])
    } catch (err: any) {
      console.error('Failed to load reviews:', err)
      // Don't show toast error for reviews as it's not critical for profile page
    } finally {
      setReviewsLoading(false)
    }
  }

  const handleSave = async () => {
    if (!profile) return

    setSaving(true)
    try {
      // Separate user data from service provider data
      const { serviceProvider, ...userData } = editData
      const updateData = {
        ...userData,
        serviceProvider: serviceProvider ? {
          ...serviceProvider,
          // Only include service provider fields that exist in database
          companyName: serviceProvider.companyName,
          description: serviceProvider.description,
          // website: serviceProvider.website, // Temporarily disabled until schema updated
          address: serviceProvider.address,
          city: serviceProvider.city,
          state: serviceProvider.state,
          postalCode: serviceProvider.postalCode,
          // Remove fields that don't exist in database:
          // contactEmail, businessHours, type, specialization, experience, website
        } : undefined
      }

      const response = await api.put('/provider-dashboard/profile', updateData)

      // After update, fetch the fresh profile data to ensure we have all fields including role
      const freshProfileResponse = await api.get('/provider-dashboard/profile')
      const apiData = freshProfileResponse.data

      // Map the API response to the expected interface structure
      const mappedProfile: ProviderProfile = {
        id: apiData.user.id,
        email: apiData.user.email,
        firstName: apiData.user.firstName,
        lastName: apiData.user.lastName,
        phone: apiData.user.phoneNumber,
        role: user?.role || 'PROVIDER', // Get role from auth context since API doesn't include it
        isVerified: apiData.user.isVerified,
        isApproved: apiData.user.isApproved,
        serviceProvider: apiData.serviceProvider ? {
          ...apiData.serviceProvider,
          contactEmail: apiData.serviceProvider.contactEmail || apiData.user.email,
          website: apiData.serviceProvider.website || null,
          businessHours: apiData.serviceProvider.businessHours || {
            monday: '9:00 AM - 5:00 PM',
            tuesday: '9:00 AM - 5:00 PM',
            wednesday: '9:00 AM - 5:00 PM',
            thursday: '9:00 AM - 5:00 PM',
            friday: '9:00 AM - 5:00 PM',
            saturday: '9:00 AM - 5:00 PM',
            sunday: 'Closed'
          },
          type: apiData.serviceProvider.serviceType,
          specialization: [],
          experience: apiData.serviceProvider.description || '',
          rating: apiData.serviceProvider.rating || 0
        } : undefined
      }

      setProfile(mappedProfile)
      setEditData(mappedProfile)
      setEditing(false)
      toast.success('Profile updated successfully')
    } catch (err: any) {
      toast.error(err.response?.data?.message || err.message || 'Failed to update profile')
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    setEditData(profile || {})
    setEditing(false)
  }

  const handleCopyProviderId = async (providerId: string) => {
    try {
      await navigator.clipboard.writeText(providerId)
      toast.success('Provider ID copied to clipboard')
    } catch (err) {
      toast.error('Failed to copy provider ID')
    }
  }

  const handleInputChange = (field: string, value: any, nested?: string) => {
    if (nested) {
      setEditData(prev => ({
        ...prev,
        [nested]: {
          ...(prev[nested as keyof ProviderProfile] as any),
          [field]: value
        }
      }))
    } else {
      setEditData(prev => ({
        ...prev,
        [field]: value
      }))
    }
  }

  // Handle address change with coordinates from Google Places
  const handleAddressChange = (address: string, coordinates?: { lat: number; lng: number }) => {
    // Update the address field
    handleInputChange('address', address, 'serviceProvider')

    // Store coordinates if available
    if (coordinates) {
      setEditData(prev => ({
        ...prev,
        serviceProvider: {
          ...(prev.serviceProvider as any),
          address,
          coordinates
        }
      }))
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    )
  }

  if (!profile) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
        Error loading profile
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="bg-primary-100 p-3 rounded-full">
              <UserCircleIcon className="h-8 w-8 text-primary-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Provider Profile</h1>
              <p className="text-gray-600">Manage your service provider information</p>
            </div>
          </div>
          {activeTab === 'profile' && (
            <div className="flex items-center space-x-3">
              {editing ? (
                <>
                  <button
                    onClick={handleCancel}
                    disabled={saving}
                    className="bg-gray-600 hover:bg-gray-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2"
                  >
                    <XMarkIcon className="h-4 w-4" />
                    <span>Cancel</span>
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2"
                  >
                    {saving ? (
                      <LoadingSpinner size="sm" />
                    ) : (
                      <CheckBadgeIcon className="h-4 w-4" />
                    )}
                    <span>Save</span>
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setEditing(true)}
                  className="bg-secondary-600 hover:bg-secondary-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2"
                >
                  <PencilIcon className="h-4 w-4" />
                  <span>Edit Profile</span>
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-lg shadow">
        <nav className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('profile')}
            className={`flex items-center px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'profile'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <UserCircleIcon className="h-4 w-4 mr-2" />
            Profile Information
          </button>
          <button
            onClick={() => setActiveTab('documents')}
            className={`flex items-center px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'documents'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <DocumentTextIcon className="h-4 w-4 mr-2" />
            Documents
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'documents' ? (
        <DocumentManagementTab serviceType={profile?.serviceProvider?.serviceType as any} />
      ) : (
        <>
          {/* Status Badges */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Account Status</h2>
            <div className="flex flex-wrap gap-3">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                profile.isApproved
                  ? 'bg-green-100 text-green-800'
                  : 'bg-yellow-100 text-yellow-800'
              }`}>
                {profile.isApproved ? 'Approved' : 'Pending Approval'}
              </span>
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                profile.isVerified
                  ? 'bg-blue-100 text-blue-800'
                  : 'bg-gray-100 text-gray-800'
              }`}>
                {profile.isVerified ? 'Verified' : 'Not Verified'}
              </span>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                {profile.role ? profile.role.replace('_', ' ').toUpperCase() : 'PROVIDER'}
              </span>
            </div>
          </div>

          {/* Performance Card */}
          {profile.serviceProvider && (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900">Performance</h2>
                <span className="text-sm text-gray-500">
                  {profile.serviceProvider.rating > 0
                    ? `Based on customer feedback`
                    : 'No ratings yet'}
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Rating Overview */}
                <div className="text-center">
                  <div className="flex items-center justify-center mb-2">
                    <span className="text-4xl font-bold text-gray-900">
                      {profile.serviceProvider.rating.toFixed(1)}
                    </span>
                    <span className="text-gray-400 text-xl ml-1">★</span>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">Overall Rating</p>
                  {/* Star Rating Display */}
                  <div className="flex justify-center mb-4">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <span
                        key={star}
                        className={`text-2xl ${
                          star <= Math.round(profile.serviceProvider.rating)
                            ? 'text-yellow-400'
                            : 'text-gray-300'
                        }`}
                      >
                        ★
                      </span>
                    ))}
                  </div>
                  {profile.serviceProvider.rating === 0 && (
                    <p className="text-sm text-gray-500 italic">
                      Start receiving bookings to earn ratings
                    </p>
                  )}
                </div>

                {/* Performance Metrics */}
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-3">Performance Highlights</h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Response Time</span>
                      <span className="text-sm font-medium text-green-600">Fast</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Service Quality</span>
                      <span className="text-sm font-medium text-blue-600">Excellent</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Customer Satisfaction</span>
                      <span className="text-sm font-medium text-purple-600">
                        {profile.serviceProvider.rating >= 4.5 ? 'Very High' :
                         profile.serviceProvider.rating >= 3.5 ? 'Good' :
                         profile.serviceProvider.rating >= 2.5 ? 'Average' :
                         profile.serviceProvider.rating > 0 ? 'Needs Improvement' : 'No Data'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Account Status</span>
                      <span className={`text-sm font-medium ${
                        profile.isApproved && profile.isVerified
                          ? 'text-green-600'
                          : profile.isApproved
                          ? 'text-yellow-600'
                          : 'text-red-600'
                      }`}>
                        {profile.isApproved && profile.isVerified ? 'Active' :
                         profile.isApproved ? 'Pending Verification' : 'Pending Approval'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Recent Reviews Section */}
              {(profile.serviceProvider.rating > 0 || reviews.length > 0) && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h3 className="text-sm font-medium text-gray-900 mb-3">Recent Customer Reviews</h3>
                  {reviewsLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <LoadingSpinner size="sm" />
                    </div>
                  ) : reviews.length > 0 ? (
                    <div className="space-y-4">
                      {reviews.slice(0, 3).map((review) => (
                        <div key={review.id} className="bg-gray-50 rounded-lg p-4">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center">
                              <div className="flex text-yellow-400">
                                {[...Array(5)].map((_, i) => (
                                  <span key={i} className="text-sm">
                                    {i < review.rating ? '★' : '☆'}
                                  </span>
                                ))}
                              </div>
                              <span className="ml-2 text-sm text-gray-600">
                                {review.rating >= 4.5 ? 'Excellent' :
                                 review.rating >= 3.5 ? 'Good' :
                                 review.rating >= 2.5 ? 'Average' :
                                 'Fair'}
                              </span>
                            </div>
                            <div className="text-xs text-gray-500">
                              {new Date(review.createdAt).toLocaleDateString()}
                            </div>
                          </div>
                          <div className="flex items-center mb-2">
                            <span className="text-sm font-medium text-gray-700">
                              {review.reviewedBy
                                ? `${review.reviewedBy.firstName} ${review.reviewedBy.lastName}`
                                : 'Anonymous User'}
                            </span>
                            {review.caseNumber && (
                              <span className="text-xs text-gray-500 ml-2">
                                • Case {review.caseNumber}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600">
                            {review.comment}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : profile.serviceProvider.rating > 0 ? (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm text-gray-500 italic text-center">
                        No detailed reviews available yet. Overall rating based on customer feedback.
                      </p>
                    </div>
                  ) : (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm text-gray-500 italic text-center">
                        No reviews yet. Start receiving bookings to earn customer feedback.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Call to Action for Low Ratings */}
              {profile.serviceProvider.rating > 0 && profile.serviceProvider.rating < 3.0 && (
                <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-yellow-800">Improve Your Rating</h3>
                      <p className="mt-1 text-sm text-yellow-700">
                        Focus on punctuality, communication, and service quality to increase customer satisfaction.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Personal Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Personal Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Name
                </label>
                {editing ? (
                  <input
                    type="text"
                    value={editData.firstName || ''}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  />
                ) : (
                  <p className="text-gray-900">{profile.firstName}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name
                </label>
                {editing ? (
                  <input
                    type="text"
                    value={editData.lastName || ''}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  />
                ) : (
                  <p className="text-gray-900">{profile.lastName}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <div className="flex items-center">
                  <EnvelopeIcon className="h-4 w-4 text-gray-400 mr-2" />
                  <p className="text-gray-900">{profile.email}</p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone
                </label>
                {editing ? (
                  <input
                    type="tel"
                    value={editData.phone || ''}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  />
                ) : (
                  <div className="flex items-center">
                    <PhoneIcon className="h-4 w-4 text-gray-400 mr-2" />
                    <p className="text-gray-900">{profile.phone || 'Not provided'}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Business Information */}
          {profile.serviceProvider && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Business Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company Name
                  </label>
                  {editing ? (
                    <input
                      type="text"
                      value={editData.serviceProvider?.companyName || ''}
                      onChange={(e) => handleInputChange('companyName', e.target.value, 'serviceProvider')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    />
                  ) : (
                    <div className="flex items-center">
                      <BuildingOfficeIcon className="h-4 w-4 text-gray-400 mr-2" />
                      <p className="text-gray-900">{profile.serviceProvider.companyName}</p>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Provider ID
                  </label>
                  <div className="flex items-center space-x-2">
                    <p className="text-gray-900 font-mono text-sm">{profile.serviceProvider?.id}</p>
                    <button
                      onClick={() => handleCopyProviderId(profile.serviceProvider?.id || '')}
                      className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                      title="Copy Provider ID"
                    >
                      <ClipboardDocumentIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Email
                  </label>
                  {editing ? (
                    <input
                      type="email"
                      value={editData.serviceProvider?.contactEmail || ''}
                      onChange={(e) => handleInputChange('contactEmail', e.target.value, 'serviceProvider')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    />
                  ) : (
                    <div className="flex items-center">
                      <EnvelopeIcon className="h-4 w-4 text-gray-400 mr-2" />
                      <p className="text-gray-900">{profile.serviceProvider.contactEmail || 'Not provided'}</p>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Website
                  </label>
                  {editing ? (
                    <input
                      type="url"
                      placeholder="https://"
                      value={editData.serviceProvider?.website || ''}
                      onChange={(e) => handleInputChange('website', e.target.value, 'serviceProvider')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    />
                  ) : (
                    <div className="flex items-center">
                      <GlobeAltIcon className="h-4 w-4 text-gray-400 mr-2" />
                      {profile.serviceProvider.website ? (
                        <a href={profile.serviceProvider.website} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-800">
                          {profile.serviceProvider.website}
                        </a>
                      ) : (
                        <p className="text-gray-900">Not provided</p>
                      )}
                    </div>
                  )}
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Business Address
                  </label>
                  {editing ? (
                    <GooglePlacesAutocomplete
                      value={editData.serviceProvider?.address || ''}
                      onChange={handleAddressChange}
                      placeholder="Enter your business address..."
                      id="business-address"
                      className="w-full"
                    />
                  ) : (
                    <div className="flex items-start">
                      <MapPinIcon className="h-4 w-4 text-gray-400 mr-2 mt-1" />
                      <p className="text-gray-900">{profile.serviceProvider.address || 'Not provided'}</p>
                    </div>
                  )}
                  {editing && (
                    <p className="mt-1 text-xs text-gray-500">
                      Start typing your address and select from the suggestions. Coordinates will be automatically saved for better matching.
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    City
                  </label>
                  {editing ? (
                    <input
                      type="text"
                      value={editData.serviceProvider?.city || ''}
                      onChange={(e) => handleInputChange('city', e.target.value, 'serviceProvider')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                      placeholder="Enter city"
                    />
                  ) : (
                    <p className="text-gray-900">{profile.serviceProvider.city || 'Not provided'}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    State
                  </label>
                  {editing ? (
                    <input
                      type="text"
                      value={editData.serviceProvider?.state || ''}
                      onChange={(e) => handleInputChange('state', e.target.value, 'serviceProvider')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                      placeholder="Enter state"
                    />
                  ) : (
                    <p className="text-gray-900">{profile.serviceProvider.state || 'Not provided'}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Postal Code
                  </label>
                  {editing ? (
                    <input
                      type="text"
                      value={editData.serviceProvider?.postalCode || ''}
                      onChange={(e) => handleInputChange('postalCode', e.target.value, 'serviceProvider')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                      placeholder="Enter postal code"
                    />
                  ) : (
                    <p className="text-gray-900">{profile.serviceProvider.postalCode || 'Not provided'}</p>
                  )}
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  {editing ? (
                    <textarea
                      value={editData.serviceProvider?.description || ''}
                      onChange={(e) => handleInputChange('description', e.target.value, 'serviceProvider')}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    />
                  ) : (
                    <p className="text-gray-900 whitespace-pre-wrap">
                      {profile.serviceProvider.description || 'No description provided'}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

  
          {/* Service Areas */}
          {profile.serviceProvider?.serviceAreas && profile.serviceProvider.serviceAreas.length > 0 && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Service Areas</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {profile.serviceProvider.serviceAreas.map((area, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <h3 className="font-medium text-gray-900">{area.stateName}</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {area.cities.length > 0 ? area.cities.join(', ') : 'All cities'}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Weekly Availability */}
          {weeklySchedule && (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center mb-4">
                <ClockIcon className="h-5 w-5 text-gray-500 mr-2" />
                <h2 className="text-lg font-semibold text-gray-900">Weekly Availability</h2>
              </div>
              {scheduleLoading ? (
                <div className="flex items-center justify-center py-8">
                  <LoadingSpinner size="sm" />
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {Object.entries(weeklySchedule)
                    .sort(([dayA], [dayB]) => {
                      const dayOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
                      return dayOrder.indexOf(dayA) - dayOrder.indexOf(dayB);
                    })
                    .map(([day, schedule]) => {
                    const dayName = day.charAt(0).toUpperCase() + day.slice(1)
                    return (
                      <div
                        key={day}
                        className={`border rounded-lg p-4 ${
                          schedule.enabled
                            ? 'border-green-200 bg-green-50'
                            : 'border-gray-200 bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="font-medium text-gray-900">{dayName}</h3>
                          <div className={`w-2 h-2 rounded-full ${
                            schedule.enabled ? 'bg-green-500' : 'bg-gray-400'
                          }`} />
                        </div>
                        <p className="text-sm">
                          {schedule.enabled && schedule.ranges.length > 0 ? (
                            <span className="text-green-700">
                              {schedule.ranges.map((range, index) => (
                                <span key={index}>
                                  {range.start} - {range.end}
                                  {index < schedule.ranges.length - 1 && ', '}
                                </span>
                              ))}
                            </span>
                          ) : (
                            <span className="text-gray-500">Unavailable</span>
                          )}
                        </p>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default ProviderProfilePage